import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  Stack,
  InputAdornment,
  Tabs,
  Tab,
  alpha,
  useTheme,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  History as HistoryIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingDown as TrendingDownIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataContext } from '../../context/DataContext';
import { format } from "date-fns";

const AdvancePaymentManagement = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { customers, addAdvancePayment, withdrawAdvance, getCustomerAdvanceHistory, advancePayments } = useContext(DataContext);
  console.log('AdvancePaymentManagement: customers:', advancePayments);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [advanceHistory, setAdvanceHistory] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [modeWiseBalances, setModeWiseBalances] = useState({
    cash: 0,
    online: 0,
    oldGold: 0,
    oldSilver: 0
  });
  const [isLoading, setIsLoading] = useState(false);

  // State for customer advance table
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('balance');
  const [sortDirection, setSortDirection] = useState('desc');

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: '',
    message: '',
    amount: 0,
    details: {},
    type: '',
    onConfirm: null
  });

  // Payment modes for Add Advance
  const [paymentModes, setPaymentModes] = useState([
    { mode: 'cash', amount: '', enabled: false, label: 'Cash' },
    { mode: 'online', amount: '', enabled: false, label: 'Online' },
    { mode: 'oldGold', amount: '', enabled: false, label: 'Old Gold' },
    { mode: 'oldSilver', amount: '', enabled: false, label: 'Old Silver' },
  ]);

  // Withdraw modes
  const [withdrawModes, setWithdrawModes] = useState([
    { mode: 'cash', amount: '', enabled: false, label: 'Cash', available: 0 },
    { mode: 'online', amount: '', enabled: false, label: 'Online', available: 0 },
    { mode: 'oldGold', amount: '', enabled: false, label: 'Old Gold', available: 0 },
    { mode: 'oldSilver', amount: '', enabled: false, label: 'Old Silver', available: 0 },
  ]);

  const [transactionDate, setTransactionDate] = useState(new Date());
  const [remarks, setRemarks] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');

  // NEW: Calculate advance balances from advancePayments array
  const calculateCustomerAdvanceBalances = useCallback(() => {
    if (!advancePayments || advancePayments.length === 0) {
      return {};
    }

    const customerBalances = {};
    console.log('Calculating customer advance balances from advancePayments:', advancePayments);
    advancePayments.forEach(advance => {
      const customerId = advance.customerId;

      // Initialize customer entry if not exists
      if (!customerBalances[customerId]) {
        customerBalances[customerId] = {
          customerId: customerId,
          customerName: advance.customerName || 'Unknown',
          balance: 0,
          totalGiven: 0,
          totalUsed: 0,
          modeBalances: {
            cash: 0,
            online: 0,
            oldGold: 0,
            oldSilver: 0
          },
          advanceCount: 0,
          advanceDetails: []
        };
      }

      // Add this advance's remaining balances
      if (advance.remainingBalances) {
        Object.entries(advance.remainingBalances).forEach(([mode, amount]) => {
          const numAmount = parseFloat(amount) || 0;
          if (customerBalances[customerId].modeBalances[mode] !== undefined) {
            customerBalances[customerId].modeBalances[mode] += numAmount;
          }
        });
      }

      // Calculate total used from usedInBills
      let usedInThisAdvance = 0;
      if (advance.usedInBills && advance.usedInBills.length > 0) {
        advance.usedInBills.forEach(use => {
          usedInThisAdvance += parseFloat(use.amountUsed) || 0;
        });
      }

      customerBalances[customerId].totalGiven += parseFloat(advance.totalAmount) || 0;
      customerBalances[customerId].totalUsed += usedInThisAdvance;
      customerBalances[customerId].advanceCount += 1;
      customerBalances[customerId].advanceDetails.push(advance);
    });

    // Calculate final balance for each customer (sum of mode balances)
    Object.keys(customerBalances).forEach(customerId => {
      const customer = customerBalances[customerId];
      customer.balance = Object.values(customer.modeBalances).reduce((sum, val) => sum + val, 0);
    });

    return customerBalances;
  }, [advancePayments]);

  // NEW: Use useMemo to compute customer advance list - SHOW ALL CUSTOMERS
  const customerAdvanceList = useMemo(() => {
    // Get calculated balances
    const balances = calculateCustomerAdvanceBalances();

    // Map ALL customers with their balances (including zero balance)
    const mappedCustomers = customers.map(customer => {
      const balanceData = balances[customer.id];
      if (balanceData) {
        return {
          ...customer,
          calculatedBalance: balanceData.balance,
          totalGiven: balanceData.totalGiven,
          totalUsed: balanceData.totalUsed,
          modeBalances: balanceData.modeBalances,
          advanceCount: balanceData.advanceCount,
          advanceDetails: balanceData.advanceDetails
        };
      }
      return {
        ...customer,
        calculatedBalance: 0,
        totalGiven: 0,
        totalUsed: 0,
        modeBalances: { cash: 0, online: 0, oldGold: 0, oldSilver: 0 },
        advanceCount: 0,
        advanceDetails: []
      };
    });

    // Sort customers
    const sorted = [...mappedCustomers].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        case 'contact':
          aValue = a.contact || '';
          bValue = b.contact || '';
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        case 'balance':
          aValue = a.calculatedBalance || 0;
          bValue = b.calculatedBalance || 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        default:
          return 0;
      }
    });

    // Filter by search term (show all customers matching search)
    if (searchTerm.trim()) {
      return sorted.filter(customer =>
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.contact?.toString().includes(searchTerm)
      );
    }

    return sorted;
  }, [customers, calculateCustomerAdvanceBalances, searchTerm, sortField, sortDirection]);

  // NEW: Calculate total advance balance across all customers (only those with balance)
  const totalAdvanceBalance = useMemo(() => {
    return customerAdvanceList.reduce((sum, customer) =>
      sum + (customer.calculatedBalance || 0), 0
    );
  }, [customerAdvanceList]);

  // Calculate mode-wise remaining balances from history
  const calculateModeWiseBalances = useCallback((history) => {
    const balances = {
      cash: 0,
      online: 0,
      oldGold: 0,
      oldSilver: 0
    };

    history.forEach(record => {
      if (record.type === 'add' && record.paymentBreakdown) {
        Object.entries(record.paymentBreakdown).forEach(([mode, amount]) => {
          if (balances[mode] !== undefined) {
            balances[mode] += amount;
          }
        });
      } else if (record.type === 'used' && record.paymentBreakdown) {
        Object.entries(record.paymentBreakdown).forEach(([mode, amount]) => {
          if (balances[mode] !== undefined) {
            balances[mode] -= amount;
          }
        });
      } else if (record.type === 'withdraw') {
        if (record.paymentBreakdown) {
          Object.entries(record.paymentBreakdown).forEach(([mode, amount]) => {
            if (balances[mode] !== undefined) {
              balances[mode] -= amount;
            }
          });
        } else if (record.paymentMode) {
          balances[record.paymentMode] -= record.amount;
        }
      }
    });

    return balances;
  }, []);

  const loadAdvanceHistory = useCallback(async () => {
    if (!selectedCustomer) return;
    const history = await getCustomerAdvanceHistory(selectedCustomer.id);
    setAdvanceHistory(history);

    let totalGiven = 0;
    let totalUsed = 0;

    history.forEach(record => {
      if (record.type === 'add') {
        totalGiven += record.amount;
      } else if (record.type === 'used' || record.type === 'withdraw') {
        totalUsed += record.amount;
      }
    });

    const balance = totalGiven - totalUsed;
    setCurrentBalance(balance);

    const modeBalances = calculateModeWiseBalances(history);
    setModeWiseBalances(modeBalances);

    setWithdrawModes(prevModes => prevModes.map(mode => ({
      ...mode,
      available: modeBalances[mode.mode] || 0,
      enabled: mode.enabled && (modeBalances[mode.mode] || 0) > 0
    })));
  }, [getCustomerAdvanceHistory, selectedCustomer, calculateModeWiseBalances]);

  // Load advance history when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      loadAdvanceHistory();
    }
  }, [selectedCustomer, loadAdvanceHistory]);

  // Calculate total advance amount from all enabled payment modes
  const getTotalAdvanceAmount = () => {
    return paymentModes
      .filter(mode => mode.enabled && mode.amount)
      .reduce((sum, mode) => sum + parseFloat(mode.amount || 0), 0);
  };

  // Calculate total withdraw amount
  const getTotalWithdrawAmount = () => {
    return withdrawModes
      .filter(mode => mode.enabled && mode.amount)
      .reduce((sum, mode) => sum + parseFloat(mode.amount || 0), 0);
  };

  // Get payment modes object for API
  const getPaymentModesObject = () => {
    const modes = {};
    paymentModes.forEach(mode => {
      if (mode.enabled && mode.amount && parseFloat(mode.amount) > 0) {
        modes[mode.mode] = parseFloat(mode.amount);
      }
    });
    return modes;
  };

  // Get withdrawal modes object for API
  const getWithdrawalModesObject = () => {
    const modes = {};
    withdrawModes.forEach(mode => {
      if (mode.enabled && mode.amount && parseFloat(mode.amount) > 0) {
        modes[mode.mode] = parseFloat(mode.amount);
      }
    });
    return modes;
  };

  // Toggle payment mode for Add
  const togglePaymentMode = (index) => {
    const updatedModes = [...paymentModes];
    updatedModes[index].enabled = !updatedModes[index].enabled;
    if (!updatedModes[index].enabled) {
      updatedModes[index].amount = '';
    }
    setPaymentModes(updatedModes);
  };

  // Update payment amount for Add
  const updatePaymentAmount = (index, amount) => {
    const updatedModes = [...paymentModes];
    updatedModes[index].amount = amount;
    setPaymentModes(updatedModes);
  };

  // Toggle withdraw mode
  const toggleWithdrawMode = (index) => {
    const updatedModes = [...withdrawModes];
    const modeBalance = updatedModes[index].available;

    if (!updatedModes[index].enabled && modeBalance <= 0) {
      setSnackbar({
        open: true,
        message: `No ${updatedModes[index].label} balance available to withdraw`,
        severity: 'warning'
      });
      return;
    }

    updatedModes[index].enabled = !updatedModes[index].enabled;
    if (!updatedModes[index].enabled) {
      updatedModes[index].amount = '';
    }
    setWithdrawModes(updatedModes);
  };

  // Update withdraw amount with validation
  const updateWithdrawAmount = (index, amount) => {
    const updatedModes = [...withdrawModes];
    const numAmount = parseFloat(amount) || 0;
    const availableBalance = updatedModes[index].available;

    if (numAmount > availableBalance) {
      setSnackbar({
        open: true,
        message: `Cannot withdraw more than available ${updatedModes[index].label} balance: ₹${availableBalance}`,
        severity: 'error'
      });
      return;
    }

    updatedModes[index].amount = amount;
    setWithdrawModes(updatedModes);
  };

  // Reset payment modes
  const resetPaymentModes = () => {
    setPaymentModes([
      { mode: 'cash', amount: '', enabled: false, label: 'Cash' },
      { mode: 'online', amount: '', enabled: false, label: 'Online' },
      { mode: 'oldGold', amount: '', enabled: false, label: 'Old Gold' },
      { mode: 'oldSilver', amount: '', enabled: false, label: 'Old Silver' },
    ]);
  };

  // Reset withdraw modes
  const resetWithdrawModes = () => {
    setWithdrawModes(withdrawModes.map(mode => ({
      ...mode,
      amount: '',
      enabled: false
    })));
  };

  // Handle sort change
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle customer selection from table
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setSelectedTab(1);
  };

  const handleAddAdvance = async () => {
    if (!selectedCustomer) {
      alert('Please select a customer first');
      return;
    }

    const enabledModes = paymentModes.filter(mode => mode.enabled && mode.amount && parseFloat(mode.amount) > 0);

    if (enabledModes.length === 0) {
      alert('Please enable at least one payment mode and enter amount');
      return;
    }

    const totalAmount = getTotalAdvanceAmount();
    const paymentModesObj = getPaymentModesObject();

    setConfirmationDialog({
      open: true,
      title: 'Confirm Advance Payment',
      message: `Are you sure you want to add advance payment of ₹${totalAmount.toFixed(2)} for ${selectedCustomer.name}?`,
      amount: totalAmount,
      details: paymentModesObj,
      type: 'add',
      onConfirm: async () => {
        setConfirmationDialog({ ...confirmationDialog, open: false });
        setIsLoading(true);

        try {
          const result = await addAdvancePayment({
            customerId: selectedCustomer.id,
            customerName: selectedCustomer.name,
            paymentModes: paymentModesObj,
            totalAmount: totalAmount,
            date: transactionDate,
            remarks: remarks,
            recordedBy: "System"
          });

          if (result.success) {
            setConfirmationDialog({
              open: true,
              title: 'Transaction Successful!',
              message: `₹${totalAmount.toFixed(2)} advance added successfully for ${selectedCustomer.name}`,
              amount: totalAmount,
              details: paymentModesObj,
              type: 'add_success',
              onConfirm: () => {
                setConfirmationDialog({ ...confirmationDialog, open: false });
                resetPaymentModes();
                setRemarks('');
                setAddDialogOpen(false);
                loadAdvanceHistory();
              }
            });
          } else {
            throw new Error(result.message || "Failed to add advance");
          }
        } catch (error) {
          setSnackbar({
            open: true,
            message: error.message || 'Failed to add advance payment',
            severity: 'error'
          });
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleWithdrawAdvance = async () => {
    if (!selectedCustomer) {
      alert('Please select a customer first');
      return;
    }

    const enabledModes = withdrawModes.filter(mode => mode.enabled && mode.amount && parseFloat(mode.amount) > 0);

    if (enabledModes.length === 0) {
      alert('Please enable at least one payment mode and enter amount to withdraw');
      return;
    }

    const totalAmount = getTotalWithdrawAmount();

    for (const mode of enabledModes) {
      const amount = parseFloat(mode.amount);
      const availableBalance = mode.available;

      if (amount > availableBalance) {
        setSnackbar({
          open: true,
          message: `Cannot withdraw ₹${amount} from ${mode.label}. Available balance: ₹${availableBalance}`,
          severity: 'error'
        });
        return;
      }
    }

    if (totalAmount > currentBalance) {
      setSnackbar({
        open: true,
        message: `Cannot withdraw more than total available balance: ₹${currentBalance}`,
        severity: 'error'
      });
      return;
    }

    if (!withdrawReason.trim()) {
      alert('Please provide a reason for withdrawal');
      return;
    }

    const withdrawalModesObj = getWithdrawalModesObject();

    setConfirmationDialog({
      open: true,
      title: 'Confirm Withdrawal',
      message: `Are you sure you want to withdraw ₹${totalAmount.toFixed(2)} from ${selectedCustomer.name}'s advance balance?`,
      amount: totalAmount,
      details: withdrawalModesObj,
      type: 'withdraw',
      onConfirm: async () => {
        setConfirmationDialog({ ...confirmationDialog, open: false });
        setIsLoading(true);

        try {
          const result = await withdrawAdvance({
            customerId: selectedCustomer.id,
            customerName: selectedCustomer.name,
            withdrawalModes: withdrawalModesObj,
            totalAmount: totalAmount,
            reason: withdrawReason,
            date: transactionDate,
          });

          if (result.success) {
            const updatedBalances = { ...modeWiseBalances };
            Object.entries(withdrawalModesObj).forEach(([mode, amount]) => {
              updatedBalances[mode] = (updatedBalances[mode] || 0) - amount;
            });
            setModeWiseBalances(updatedBalances);

            const newTotalBalance = currentBalance - totalAmount;
            setCurrentBalance(newTotalBalance);

            const newHistoryRecord = {
              id: Date.now(),
              type: 'withdraw',
              amount: totalAmount,
              paymentBreakdown: withdrawalModesObj,
              reason: withdrawReason,
              date: format(transactionDate, "dd/MM/yyyy"),
              timestamp: new Date().toISOString()
            };
            setAdvanceHistory(prev => [newHistoryRecord, ...prev]);

            setConfirmationDialog({
              open: true,
              title: 'Withdrawal Successful!',
              message: `₹${totalAmount.toFixed(2)} withdrawn successfully from ${Object.keys(withdrawalModesObj).join(', ')} for ${selectedCustomer.name}`,
              amount: totalAmount,
              details: withdrawalModesObj,
              type: 'withdraw_success',
              onConfirm: () => {
                setConfirmationDialog({ ...confirmationDialog, open: false });
                resetWithdrawModes();
                setWithdrawReason('');
                setWithdrawDialogOpen(false);

                setWithdrawModes(prevModes => prevModes.map(mode => ({
                  ...mode,
                  available: updatedBalances[mode.mode] || 0,
                  enabled: false,
                  amount: ''
                })));
              }
            });
          } else {
            throw new Error(result.message || "Failed to withdraw");
          }
        } catch (error) {
          console.error('Withdrawal error:', error);
          setSnackbar({
            open: true,
            message: error.message || 'Failed to withdraw advance',
            severity: 'error'
          });
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const getTotalAdvanceGiven = () => {
    return advanceHistory
      .filter(record => record.type === 'add')
      .reduce((sum, record) => sum + record.amount, 0);
  };

  const getTotalAdvanceUsed = () => {
    return advanceHistory
      .filter(record => record.type === 'used' || record.type === 'withdraw')
      .reduce((sum, record) => sum + record.amount, 0);
  };

  const hasAnyBalance = () => {
    return Object.values(modeWiseBalances).some(balance => balance > 0);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: '100vh', py: 3, px: 2 }}>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              background: isDark
                ? "linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)"
                : "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 4,
            }}
          >
            Advance Payment Management
          </Typography>

          <Grid container spacing={3}>
            {/* Customer Advance List Table - Left Column */}
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2, borderRadius: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={700}>
                    Customer Advance List
                  </Typography>
                  <Chip
                    label={`Total: ₹${totalAdvanceBalance.toFixed(2)}`}
                    color="primary"
                    size="small"
                  />
                </Stack>

                {/* Search Bar */}
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {/* Customer Table */}
                <TableContainer sx={{ maxHeight: '500px', overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'name'}
                            direction={sortField === 'name' ? sortDirection : 'asc'}
                            onClick={() => handleSort('name')}
                          >
                            Customer
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'contact'}
                            direction={sortField === 'contact' ? sortDirection : 'asc'}
                            onClick={() => handleSort('contact')}
                          >
                            Phone
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={sortField === 'balance'}
                            direction={sortField === 'balance' ? sortDirection : 'desc'}
                            onClick={() => handleSort('balance')}
                          >
                            Balance
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customerAdvanceList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              {searchTerm ? 'No customers found matching search' : 'No customers available'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        customerAdvanceList.map((customer) => {
                          const hasBalance = customer.calculatedBalance > 0;
                          return (
                            <TableRow
                              key={customer.id}
                              hover
                              sx={{
                                cursor: 'pointer',
                                backgroundColor: selectedCustomer?.id === customer.id
                                  ? alpha(theme.palette.primary.main, 0.08)
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                },
                                opacity: hasBalance ? 1 : 0.7,
                              }}
                              onClick={() => handleCustomerSelect(customer)}
                            >
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="body2" fontWeight={hasBalance ? 600 : 400}>
                                    {customer.name}
                                  </Typography>
                                  {!hasBalance && (
                                    <Chip
                                      label="No Balance"
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.6rem' }}
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {customer.contact || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  fontWeight={hasBalance ? 600 : 400}
                                  color={hasBalance ? "primary.main" : "text.secondary"}
                                >
                                  ₹{(customer.calculatedBalance || 0).toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Stats Footer */}
                {customerAdvanceList.length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Total Customers: <strong>{customerAdvanceList.length}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        With Balance: <strong>{customerAdvanceList.filter(c => c.calculatedBalance > 0).length}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Outstanding: <strong>₹{totalAdvanceBalance.toFixed(2)}</strong>
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Card>
            </Grid>

            {/* Right Column - Customer Details */}
            <Grid item xs={12} md={8}>
              {selectedCustomer ? (
                <>
                  <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {selectedCustomer.name}
                        </Typography>
                        <Typography variant="h3" fontWeight={800}>
                          ₹{currentBalance.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Contact: {selectedCustomer?.contact || 'N/A'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            resetPaymentModes();
                            setAddDialogOpen(true);
                          }}
                          sx={{ borderRadius: 2 }}
                        >
                          Add Advance
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<TrendingDownIcon />}
                          onClick={() => {
                            resetWithdrawModes();
                            setWithdrawDialogOpen(true);
                          }}
                          disabled={currentBalance <= 0 || !hasAnyBalance()}
                          sx={{ borderRadius: 2 }}
                        >
                          Withdraw
                        </Button>
                      </Stack>
                    </Stack>
                  </Card>

                  <Card sx={{ borderRadius: 3 }}>
                    <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ px: 2, pt: 2 }}>
                      <Tab icon={<HistoryIcon />} label="Transaction History" />
                      <Tab icon={<WalletIcon />} label="Summary" />
                    </Tabs>

                    <Box sx={{ p: 3 }}>
                      {selectedTab === 0 && (
                        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Payment Details</TableCell>
                                <TableCell>Remarks/Reason</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {advanceHistory.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={5} align="center">
                                    No advance payment history found
                                  </TableCell>
                                </TableRow>
                              ) : (
                                advanceHistory.map((record, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{record.date}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={record.type === 'add' ? 'Advance Added' : record.type === 'withdraw' ? 'Withdrawn' : `Used in Bill ${record.billNo ? `#${record.billNo}` : ''}`}
                                        size="small"
                                        color={record.type === 'add' ? 'success' : record.type === 'withdraw' ? 'error' : 'info'}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography sx={{ fontWeight: 600, color: record.type === 'add' ? 'green' : 'red' }}>
                                        {record.type === 'add' ? '+' : '-'} ₹{record.amount.toFixed(2)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {record.paymentBreakdown ? (
                                        <Stack spacing={0.5}>
                                          {Object.entries(record.paymentBreakdown).map(([mode, amount]) => (
                                            amount > 0 && (
                                              <Typography key={mode} variant="caption" sx={{ display: 'block' }}>
                                                <strong>{mode === 'oldGold' ? 'Old Gold' : mode === 'oldSilver' ? 'Old Silver' : mode.charAt(0).toUpperCase() + mode.slice(1)}:</strong> ₹{amount}
                                              </Typography>
                                            )
                                          ))}
                                        </Stack>
                                      ) : (
                                        record.paymentMode || '-'
                                      )}
                                    </TableCell>
                                    <TableCell>{record.remarks || record.reason || '-'}</TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      {selectedTab === 1 && (
                        <Stack spacing={3}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                                <Typography variant="subtitle2" color="text.secondary">Total Advance Given</Typography>
                                <Typography variant="h5" fontWeight={700} color="success.main">
                                  ₹{getTotalAdvanceGiven().toFixed(2)}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                                <Typography variant="subtitle2" color="text.secondary">Total Advance Used</Typography>
                                <Typography variant="h5" fontWeight={700} color="error.main">
                                  ₹{getTotalAdvanceUsed().toFixed(2)}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                                <Typography variant="subtitle2" color="text.secondary">Current Balance</Typography>
                                <Typography variant="h5" fontWeight={700} color="primary.main">
                                  ₹{currentBalance.toFixed(2)}
                                </Typography>
                              </Paper>
                            </Grid>
                          </Grid>

                          <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="subtitle1" fontWeight={700} mb={2}>
                              Mode-wise Balance Breakdown
                            </Typography>
                            <Grid container spacing={2}>
                              {[
                                { key: 'cash', label: 'Cash', color: 'success' },
                                { key: 'online', label: 'Online', color: 'info' },
                                { key: 'oldGold', label: 'Old Gold', color: 'warning' },
                                { key: 'oldSilver', label: 'Old Silver', color: 'secondary' }
                              ].map(({ key, label, color }) => {
                                const balance = modeWiseBalances[key];
                                const isZero = balance === 0;
                                const isNegative = balance < 0;

                                return (
                                  <Grid item xs={6} sm={4} md={3} key={key}>
                                    <Box sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: isNegative ? alpha(theme.palette.error.main, 0.1) :
                                        isZero ? alpha(theme.palette.grey[500], 0.1) :
                                          alpha(theme.palette[color].main, 0.1),
                                      border: `1px solid ${alpha(isNegative ? theme.palette.error.main :
                                        isZero ? theme.palette.grey[500] :
                                          theme.palette[color].main, 0.2)}`
                                    }}>
                                      <Typography variant="caption" color="text.secondary">
                                        {label}
                                      </Typography>
                                      <Typography variant="h6" fontWeight={700}
                                        color={isNegative ? 'error.main' : isZero ? 'text.secondary' : `${color}.main`}>
                                        ₹{balance.toFixed(2)}
                                      </Typography>
                                      {isZero && <Typography variant="caption" color="text.secondary">No balance</Typography>}
                                      {isNegative && <Typography variant="caption" color="error.main">Overdrawn!</Typography>}
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Paper>

                          <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="subtitle1" fontWeight={700} mb={2}>
                              Recent Transactions
                            </Typography>
                            <TableContainer sx={{ borderRadius: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                    <TableCell><strong>Date</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Amount</strong></TableCell>
                                    <TableCell><strong>Mode-wise Breakdown</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {advanceHistory.slice(0, 5).map((record, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{record.date}</TableCell>
                                      <TableCell>
                                        <Chip
                                          label={record.type === 'add' ? 'Added' : record.type === 'used' ? 'Used in Bill' : 'Withdrawn'}
                                          size="small"
                                          color={record.type === 'add' ? 'success' : 'error'}
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography sx={{ fontWeight: 600, color: record.type === 'add' ? '#22c55e' : '#ef4444' }}>
                                          {record.type === 'add' ? '+' : '-'} ₹{record.amount.toFixed(2)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        {record.paymentBreakdown ? (
                                          <Stack spacing={0.5}>
                                            {Object.entries(record.paymentBreakdown).map(([mode, amount]) => (
                                              amount > 0 && (
                                                <Typography key={mode} variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                  <strong>{mode === 'oldGold' ? 'Old Gold' : mode === 'oldSilver' ? 'Old Silver' : mode.charAt(0).toUpperCase() + mode.slice(1)}:</strong> ₹{amount}
                                                </Typography>
                                              )
                                            ))}
                                          </Stack>
                                        ) : '-'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {advanceHistory.length === 0 && (
                                    <TableRow>
                                      <TableCell colSpan={4} align="center">
                                        <Typography variant="body2" color="text.secondary" py={3}>
                                          No transactions found
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        </Stack>
                      )}
                    </Box>
                  </Card>
                </>
              ) : (
                <Card sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
                  <WalletIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Select a customer from the list to view and manage advance payments
                  </Typography>
                  {customerAdvanceList.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No customers have advance payments yet
                    </Typography>
                  )}
                </Card>
              )}
            </Grid>
          </Grid>

          {/* All Dialogs remain the same as your existing code */}
          {/* Add Advance Dialog */}
          <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddIcon color="success" />
                <Typography variant="h6">Add Advance Payment</Typography>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Customer: <strong>{selectedCustomer?.name}</strong>
                </Typography>

                <Divider>Payment Modes</Divider>

                <Stack spacing={2}>
                  {paymentModes.map((payment, index) => (
                    <Stack key={payment.mode} direction="row" spacing={2} alignItems="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={payment.enabled}
                            onChange={() => togglePaymentMode(index)}
                            size="small"
                          />
                        }
                        label={payment.label}
                        sx={{ width: 100 }}
                      />
                      <TextField
                        label="Amount (₹)"
                        value={payment.amount}
                        onChange={(e) => updatePaymentAmount(index, e.target.value)}
                        disabled={!payment.enabled}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Stack>
                  ))}
                </Stack>

                {getTotalAdvanceAmount() > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Total Advance Amount: <strong>₹{getTotalAdvanceAmount().toFixed(2)}</strong>
                  </Alert>
                )}

                <DatePicker
                  label="Transaction Date"
                  value={transactionDate}
                  onChange={(date) => setTransactionDate(date)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />

                <TextField
                  label="Remarks (Optional)"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  size="small"
                  placeholder="e.g., Collected Person name, reason for advance, etc."
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleAddAdvance}
                variant="contained"
                color="success"
                disabled={getTotalAdvanceAmount() === 0}
              >
                Add Advance (₹{getTotalAdvanceAmount().toFixed(2)})
              </Button>
            </DialogActions>
          </Dialog>

          {/* Withdraw Advance Dialog */}
          <Dialog open={withdrawDialogOpen} onClose={() => setWithdrawDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TrendingDownIcon color="error" />
                <Typography variant="h6">Withdraw Advance</Typography>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Customer: <strong>{selectedCustomer?.name}</strong>
                </Typography>

                <Alert severity="info">
                  Total Available Balance: <strong>₹{currentBalance.toFixed(2)}</strong>
                </Alert>

                <Divider>Select Withdrawal Modes</Divider>

                <Stack spacing={2}>
                  {withdrawModes.map((mode, index) => {
                    const hasBalance = mode.available > 0;
                    return (
                      <Stack key={mode.mode} direction="row" spacing={2} alignItems="center">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mode.enabled}
                              onChange={() => toggleWithdrawMode(index)}
                              size="small"
                              disabled={!hasBalance}
                            />
                          }
                          label={`${mode.label} (Available: ₹${mode.available.toFixed(2)})`}
                          sx={{ width: 180 }}
                        />
                        <TextField
                          label="Amount to Withdraw (₹)"
                          value={mode.amount}
                          onChange={(e) => updateWithdrawAmount(index, e.target.value)}
                          disabled={!mode.enabled}
                          size="small"
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>

                {getTotalWithdrawAmount() > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Total Withdrawal Amount: <strong>₹{getTotalWithdrawAmount().toFixed(2)}</strong>
                    {getTotalWithdrawAmount() > currentBalance && (
                      <Typography color="error" variant="caption" display="block">
                        Exceeds total available balance!
                      </Typography>
                    )}
                  </Alert>
                )}

                <DatePicker
                  label="Withdrawal Date"
                  value={transactionDate}
                  format='dd/MM/yyyy'
                  onChange={(date) => setTransactionDate(date)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />

                <TextField
                  label="Reason for Withdrawal *"
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  required
                  placeholder="e.g., Customer requested refund, Business expense, etc."
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleWithdrawAdvance}
                variant="contained"
                color="error"
                disabled={
                  getTotalWithdrawAmount() === 0 ||
                  getTotalWithdrawAmount() > currentBalance ||
                  !withdrawReason.trim()
                }
              >
                Withdraw Advance (₹{getTotalWithdrawAmount().toFixed(2)})
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Loading Overlay */}
          {isLoading && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Card sx={{ p: 4, borderRadius: 3, minWidth: 300, textAlign: 'center' }}>
                <Box sx={{ display: 'inline-block' }}>
                  <div className="loader" style={{
                    width: '48px',
                    height: '48px',
                    border: '5px solid #f3f3f3',
                    borderTop: '5px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 20px'
                  }} />
                </Box>
                <Typography variant="h6" gutterBottom>
                  Processing Transaction...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we complete your request
                </Typography>
              </Card>
            </Box>
          )}

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmationDialog.open}
            onClose={() => {
              if (!isLoading && confirmationDialog.type !== 'add_success' && confirmationDialog.type !== 'withdraw_success') {
                setConfirmationDialog({ ...confirmationDialog, open: false });
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                {confirmationDialog.type === 'add' && <AddIcon color="success" />}
                {confirmationDialog.type === 'withdraw' && <TrendingDownIcon color="error" />}
                {(confirmationDialog.type === 'add_success' || confirmationDialog.type === 'withdraw_success') && (
                  <Box sx={{ color: '#4caf50' }}>✓</Box>
                )}
                <Typography variant="h6">{confirmationDialog.title}</Typography>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Typography variant="body1">
                  {confirmationDialog.message}
                </Typography>

                {(confirmationDialog.type === 'add_success' || confirmationDialog.type === 'withdraw_success') && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Transaction Details:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <Stack spacing={1}>
                          <Typography variant="body2">
                            <strong>Customer:</strong> {selectedCustomer?.name}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Amount:</strong> ₹{confirmationDialog.amount?.toFixed(2)}
                          </Typography>
                          {confirmationDialog.details && Object.keys(confirmationDialog.details).length > 0 && (
                            <Box>
                              <Typography variant="body2" gutterBottom>
                                <strong>Payment Breakdown:</strong>
                              </Typography>
                              <Stack spacing={0.5} sx={{ pl: 2 }}>
                                {Object.entries(confirmationDialog.details).map(([mode, amount]) => (
                                  amount > 0 && (
                                    <Typography key={mode} variant="body2">
                                      • {mode === 'oldGold' ? 'Old Gold' : mode === 'oldSilver' ? 'Old Silver' : mode.charAt(0).toUpperCase() + mode.slice(1)}: ₹{amount}
                                    </Typography>
                                  )
                                ))}
                              </Stack>
                            </Box>
                          )}
                          <Typography variant="body2">
                            <strong>Date:</strong> {format(transactionDate, 'dd/MM/yyyy')}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Box>
                  </>
                )}

                {confirmationDialog.type === 'add' && confirmationDialog.details && Object.keys(confirmationDialog.details).length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Payment Breakdown:
                      </Typography>
                      <Stack spacing={1}>
                        {Object.entries(confirmationDialog.details).map(([mode, amount]) => (
                          amount > 0 && (
                            <Typography key={mode} variant="body2">
                              • {mode === 'oldGold' ? 'Old Gold' : mode === 'oldSilver' ? 'Old Silver' : mode.charAt(0).toUpperCase() + mode.slice(1)}: ₹{amount}
                            </Typography>
                          )
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}

                {confirmationDialog.type === 'withdraw' && confirmationDialog.details && Object.keys(confirmationDialog.details).length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Withdrawal Breakdown:
                      </Typography>
                      <Stack spacing={1}>
                        {Object.entries(confirmationDialog.details).map(([mode, amount]) => (
                          amount > 0 && (
                            <Typography key={mode} variant="body2">
                              • {mode === 'oldGold' ? 'Old Gold' : mode === 'oldSilver' ? 'Old Silver' : mode.charAt(0).toUpperCase() + mode.slice(1)}: ₹{amount}
                            </Typography>
                          )
                        ))}
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        <strong>Reason:</strong> {withdrawReason}
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              {(confirmationDialog.type === 'add_success' || confirmationDialog.type === 'withdraw_success') ? (
                <Button
                  onClick={confirmationDialog.onConfirm}
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  OK
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => setConfirmationDialog({ ...confirmationDialog, open: false })}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmationDialog.onConfirm}
                    variant="contained"
                    color={confirmationDialog.type === 'add' ? 'success' : 'error'}
                    disabled={isLoading}
                  >
                    Confirm {confirmationDialog.type === 'add' ? 'Add' : 'Withdraw'}
                  </Button>
                </>
              )}
            </DialogActions>
          </Dialog>

          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AdvancePaymentManagement;